import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";

interface WebflowSite {
  id: string;
  name: string;
  workspaceId: string;
}

export default function WebflowConnect() {
  const [params] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();
  const ref = params.get("ref") ?? "";
  const sitesParam = params.get("sites") ?? "";
  const errorParam = params.get("error");

  const [sites, setSites] = useState<WebflowSite[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(errorParam ?? null);

  useEffect(() => {
    if (sitesParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(sitesParam));
        setSites(Array.isArray(parsed) ? parsed : []);
        if (parsed.length === 1) setSelected(parsed[0].id);
      } catch {
        setError("Failed to parse site list from Webflow.");
      }
    }
  }, [sitesParam]);

  const handleComplete = async () => {
    if (!selected) return;
    const site = sites.find(s => s.id === selected);
    if (!site) return;

    setSaving(true);
    try {
      const resp = await api.admin.post("Webflow/OAuth/Complete", {
        ref,
        webflowSiteId: site.id,
        webflowSiteName: site.name,
        webflowWorkspaceId: site.workspaceId
      });
      if (!resp.ok) {
        const body = await resp.text();
        setError(`Failed to save connection: ${body}`);
        return;
      }
      navigate("/integrations/webflow");
    } catch (err) {
      setError(`Connection error: ${String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  if (errorParam && !sites.length) {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center", padding: 24 }}>
        <h3>Connection Failed</h3>
        <p style={{ color: "#991B1B" }}>{error}</p>
        <PrimaryBlueButton label="Back to Webflow" onClick={() => navigate("/integrations/webflow")} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 540, margin: "60px auto", padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
        <img src="/img/webflow.svg" alt="Webflow" style={{ width: 36, height: 36 }} />
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Connect Webflow Site</h2>
      </div>

      {error && (
        <div style={{ marginBottom: 20, padding: "12px 16px", background: "#FEE2E2", borderRadius: 8, color: "#991B1B" }}>
          {error}
        </div>
      )}

      {sites.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <LoadingSpinner />
          <p style={{ marginTop: 16, color: "var(--color-text-secondary)" }}>Loading your Webflow sites…</p>
        </div>
      ) : (
        <>
          <p style={{ marginBottom: 20, color: "var(--color-text-secondary)" }}>
            Select the Webflow site where you want to embed the Velaro chat widget.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
            {sites.map(site => (
              <label
                key={site.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 18px",
                  border: `2px solid ${selected === site.id ? "var(--color-primary)" : "var(--color-border)"}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  background: selected === site.id ? "var(--color-primary-light, #EEF2FF)" : "var(--color-surface)"
                }}
              >
                <input
                  type="radio"
                  name="site"
                  value={site.id}
                  checked={selected === site.id}
                  onChange={() => setSelected(site.id)}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>{site.name}</div>
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{site.id}</div>
                </div>
              </label>
            ))}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <PrimaryBlueButton
              label={saving ? "Connecting…" : "Connect Site"}
              onClick={handleComplete}
              disabled={!selected || saving}
            />
            <SecondaryBlueButton
              label="Cancel"
              onClick={() => navigate("/integrations/webflow")}
              disabled={saving}
            />
          </div>
        </>
      )}
    </div>
  );
}
